/**
 * FormFlu Tenant Context
 * 
 * Manages the current tenant (organization) context for the application.
 * Users can belong to multiple tenants and switch between them.
 * 
 * HIPAA COMPLIANCE NOTES:
 * - Tenant metadata is not PHI
 * - Tenant switching generates new JWT with different scope
 * - All tenant-scoped data is isolated in separate database schemas
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { TenantListItem, Tenant } from '@formflow/shared';
import { tenantApi, ApiClientError } from '../lib/api';
import { useAuth } from './AuthContext';

// =============================================================================
// Types
// =============================================================================

/** Tenant context state */
export interface TenantState {
  /** List of tenants user belongs to */
  tenants: TenantListItem[];
  /** Currently active tenant */
  currentTenant: Tenant | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Is tenant data loaded? */
  isInitialized: boolean;
}

/** Tenant context value */
export interface TenantContextValue extends TenantState {
  /** Switch to a different tenant */
  switchTenant: (tenantSlug: string) => Promise<void>;
  /** Refresh tenant list */
  refreshTenants: () => Promise<void>;
  /** Clear error */
  clearError: () => void;
  /** Check if user has multiple tenants */
  hasMultipleTenants: boolean;
}

// =============================================================================
// Context
// =============================================================================

const TenantContext = createContext<TenantContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { isAuthenticated } = useAuth();
  
  const [state, setState] = useState<TenantState>({
    tenants: [],
    currentTenant: null,
    isLoading: false,
    error: null,
    isInitialized: false,
  });

  // Load tenants when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset state when user logs out
      setState({
        tenants: [],
        currentTenant: null,
        isLoading: false,
        error: null,
        isInitialized: false,
      });
      return;
    }

    loadTenants();
  }, [isAuthenticated]);

  // Load user's tenants
  const loadTenants = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Load tenant list
      const tenantList = await tenantApi.listUserTenants();
      
      // Try to get current tenant from JWT context
      let current: Tenant | null = null;
      try {
        current = await tenantApi.getCurrentTenant();
      } catch (err) {
        // Current tenant might not be set yet (first login)
        // Use first tenant from list as default
        if (tenantList.length > 0) {
          // Convert TenantListItem to Tenant-like object for display
          current = {
            id: tenantList[0].id,
            name: tenantList[0].name,
            slug: tenantList[0].slug,
            role: tenantList[0].role,
            is_active: true,
          };
        }
      }

      setState(prev => ({
        ...prev,
        tenants: tenantList,
        currentTenant: current,
        isLoading: false,
        isInitialized: true,
      }));
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to load organizations';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
        isInitialized: true,
      }));
    }
  }, []);

  // Switch to a different tenant
  const switchTenant = useCallback(async (tenantSlug: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await tenantApi.switchTenant(tenantSlug);
      
      setState(prev => ({
        ...prev,
        currentTenant: response.tenant,
        isLoading: false,
      }));

      // Reload the page to refresh all data with new tenant context
      // This ensures all cached data is cleared and fresh data is loaded
      window.location.reload();
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to switch organization';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      
      throw e;
    }
  }, []);

  // Refresh tenant list
  const refreshTenants = useCallback(async () => {
    await loadTenants();
  }, [loadTenants]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Check if user has multiple tenants
  const hasMultipleTenants = useMemo(() => {
    return state.tenants.length > 1;
  }, [state.tenants]);

  const value = useMemo((): TenantContextValue => ({
    ...state,
    switchTenant,
    refreshTenants,
    clearError,
    hasMultipleTenants,
  }), [state, switchTenant, refreshTenants, clearError, hasMultipleTenants]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access tenant context
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

/**
 * Hook to get current tenant
 */
export function useCurrentTenant(): Tenant | null {
  const { currentTenant } = useTenant();
  return currentTenant;
}

/**
 * Hook to get tenant list
 */
export function useTenantList(): TenantListItem[] {
  const { tenants } = useTenant();
  return tenants;
}

/**
 * Hook to check if tenant is loading
 */
export function useTenantLoading(): boolean {
  const { isLoading } = useTenant();
  return isLoading;
}
